import {
  ClientDetailsDto,
  ClientDto,
  SurveyV2,
} from '@act/shared/data-transfer-objects';
import { formatPhoneNumber } from '@act/shared/utilities';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { clientsRequests } from 'redux/requests/clients.requests';
import { useApiCall } from 'redux/utils/api-hook';
import { LoadingPage } from '../../../../stories/utilities/action-menu/loading-page';
import styled from 'styled-components';
import { useAppDispatch } from 'redux/store-hooks';
import { getAllSurveys, surveysSelectors } from 'redux/slices/surveys.slice';

const getCustomerFields = (
  clientDetails: ClientDetailsDto,
  allSurveys: SurveyV2.SurveyDto[]
) => {
  const cleanTeams = clientDetails?.teams?.map(team => ({
    ...team,
    users: [...team.users].sort((a, b) => {
      // Put managers at the top
      if (a.isManager && !b.isManager) return -1;
      if (!a.isManager && b.isManager) return 1;

      // Then sort alphabetically by first name
      return a.firstName.localeCompare(b.firstName);
    }),
  }));

  const clientSurveys =
    allSurveys?.length && clientDetails
      ? allSurveys.filter(survey =>
          survey.assignmentConfiguration?.clientIds?.includes(clientDetails?.id)
        )
      : [];

  return [
    {
      title: 'Name',
      value: clientDetails.name,
      error: !clientDetails.name ? 'Name is required' : null,
    },
    {
      title: 'Display Name',
      value: clientDetails.displayName,
      error: !clientDetails.displayName ? 'Display Name is required' : null,
    },
    {
      title: 'Website',
      value: clientDetails.website,
      error: !clientDetails.website
        ? 'Website is required'
        : !clientDetails.website.startsWith('reemahealth.com')
        ? 'Website should start with "reemahealth.com"'
        : null,
    },
    {
      title: 'Market Numbers',
      value: clientDetails.marketNumber?.number
        ? formatPhoneNumber(clientDetails.marketNumber?.number) +
          ' [' +
          clientDetails.marketNumber?.displayName +
          ']'
        : null,
      error: !clientDetails.marketNumber?.number
        ? 'Market Number is required'
        : null,
    },
    {
      title: 'Feedback Number',
      value: clientDetails.feedbackNumber?.number
        ? formatPhoneNumber(clientDetails.feedbackNumber?.number) +
          ' [' +
          clientDetails.feedbackNumber?.displayName +
          ']'
        : null,
      error: !clientDetails.feedbackNumber?.number
        ? 'Feedback Number is required'
        : null,
    },
    {
      title: 'Avon Medical Center ID',
      value: clientDetails.avonMedicalCenterId,
    },
    {
      title: 'SMS Re-Engagement Threshold',
      value: clientDetails.smsReEngagementThreshold,
      error: !clientDetails.smsReEngagementThreshold
        ? 'SMS Re-Engagement Threshold is required'
        : null,
    },
    {
      title: 'Call Engagement Length Requirement in Minutes',
      value: clientDetails.callEngagementLengthRequirementInMinutes,
      error: !clientDetails.callEngagementLengthRequirementInMinutes
        ? 'Call Engagement Length Requirement in Minutes is required'
        : null,
    },
    {
      title: 'Average Months in Program',
      value: clientDetails.averageMonthsInProgram,
      error: !clientDetails.averageMonthsInProgram
        ? 'Average Months in Program is required'
        : null,
    },
    {
      title: 'Consents',
      value: clientDetails.terms?.length && (
        <ul>
          {clientDetails.terms?.map(term => (
            <li key={term.id}>{term.name}</li>
          ))}
        </ul>
      ),
      error:
        !clientDetails.terms || !clientDetails.terms.length
          ? 'No Consents configured for customer'
          : null,
    },
    {
      title: 'Surveys',
      value: clientSurveys?.length && (
        <ul>
          {clientSurveys?.map(survey => (
            <li key={survey.id}>{survey.name}</li>
          ))}
        </ul>
      ),
      error: clientSurveys?.length
        ? null
        : 'No surveys configured for customer',
    },
    {
      title: 'Teams',
      value: clientDetails?.teams?.length && (
        <>
          {cleanTeams?.map(team => (
            <Field
              key={team.id}
              title={team.name}
              value={<TeamUserList users={team.users} />}
              error={getTeamError(team)}
            />
          ))}
        </>
      ),
      error: cleanTeams?.length ? null : 'No teams configured for customer',
    },
  ];
};

export const FieldDiv = styled.div`
  margin-bottom: 20px;

  .field-content {
    margin-left: 20px;
  }

  .field-value {
  }
  .field-title {
    font-weight: bold;
  }

  .field-error {
    color: red;
  }

  ul {
    margin: 0;
  }
`;

const Field = ({
  title,
  value,
  error,
}: {
  title: string;
  value: string | number | React.ReactElement;
  error?: string;
}) => {
  return (
    <FieldDiv>
      <div className="field-title">{title}</div>
      <div className="field-content" style={{ marginLeft: 20 }}>
        {error && <div className="field-error">{error}</div>}
        <div className="field-value">{value ? value : '-'}</div>
      </div>
    </FieldDiv>
  );
};

const TeamUserList = ({
  users,
}: {
  users: ClientDetailsDto['teams'][number]['users'][number][];
}) => {
  if (!users?.length) {
    return null;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Phone #</TableCell>
          <TableCell>Resourcing #</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map(user => (
          <TableRow key={user.id}>
            <TableCell>
              {user.firstName} {user.lastName}
              {user.isManager && <strong> (Manager)</strong>}
            </TableCell>
            <TableCell>
              {user.phoneNumber ? formatPhoneNumber(user.phoneNumber) : '-'}
            </TableCell>
            <TableCell>
              {user.resourcingNumber
                ? formatPhoneNumber(user.resourcingNumber)
                : '-'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const getTeamError = (team: ClientDetailsDto['teams'][number]): string => {
  if (!team.users?.length) {
    return 'Team has no users';
  }
  if (!team.users[0].isManager) {
    return 'Team has no manager';
  }
  return null;
};

export const CustomerDetails = ({ client }: { client: ClientDto }) => {
  // Get Client Details
  const [clientDetails, clientDetailsLoading, getClientDetails] = useApiCall(
    clientsRequests.getClientDetails
  );
  useEffect(() => {
    if (client) {
      getClientDetails({ params: { id: client.id } });
    }
  }, [client]);

  // Get All Surveys
  const allSurveys = surveysSelectors.useSelectAllSurveys();
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getAllSurveys({}));
  }, [dispatch]);

  const clientFields = useMemo(() => {
    if (!clientDetails) {
      return [];
    }
    return getCustomerFields(clientDetails, allSurveys);
  }, [clientDetails, allSurveys]);

  if (clientDetailsLoading) {
    return <LoadingPage />;
  }

  return (
    <div>
      {clientFields.map(field => (
        <Field key={field.title} {...field} />
      ))}
    </div>
  );
};
